import { FC, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams, Prompt } from 'react-router-dom';
import { useFormik, Form, FormikProvider, Field } from 'formik';
import { serialize } from 'object-to-formdata';
import clsx from 'clsx';
import { useQuery } from 'react-query';

import * as CompaniesRedux from '../../modules/companies/store/UserCompaniesRedux';
import { Input } from '../../shared/Input';
import { SelectOptions } from '../../types/SelectOptions';
import { CompanyTypes } from '../../types/CompanyTypes';
// import { CompanyModel } from '../../modules/companies/CompanyModel';
import { RootState } from '../../../setup/redux/Store';
import { ICompany } from '../../types/companies';
import { FileUploader } from './CompanyDetails/components/FileUploader';
import { LogoUploader } from './CompanyDetails/components/LogoUploader';
import { ReportsPeriodTypeMap } from '../../../_metronic/constants/general';
import { toSelectOption } from '../../../_metronic/helpers/functions/Select';
import DatePicker from 'react-datepicker';
import { ErrorMessage } from '../../shared/ErrorMessage';
import { Label } from '../../shared/Label';

import { axiosInstance } from '../../../network/apis';
import { DisplayErrors } from '../../../utils/DisplayErrors';
import { toast } from 'react-toastify';

import AhadDialog from '../../../setup/shared/AhadDialog';
import { Portal } from '../../../_metronic/partials';
// import { PrivateRoutes } from '../../../_metronic/constants/ROUTES';
import useExitPrompt from '../../hooks/useExitPrompt/useExitPrompt';
import { $t } from '../../../_metronic/i18n/formatMessage';
import { usePermissions } from '../../hooks/usePermissions';
import { useEditViewCompanyDetailsWrapperSchema } from './useEditViewCompanyDetailsWrapperSchema';
import { countires } from '../../../_metronic/constants/countries';
import { SeparatorLines } from '../../shared/Lines';

const fileMaxSize = 2 * 1024;
const allowedFiles = ['application/pdf', 'image/jpeg', 'jpeg', 'png', 'image/png', 'image/svg+xml'];
const maxFileSizeInMegaBytes = 2;

export const EditViewCompanyDetailsWrapper: FC<any> = ({ mode, displayRequiredSection }) => {
  const { schema, initialValues } = useEditViewCompanyDetailsWrapperSchema();
  const { userCan, userCanNot } = usePermissions('view-company');
  const { setShowExitPrompt, setDisable } = useExitPrompt(false);
  // Corporate
  // const [tax_number_document, setTax_number_document] = useState<any>('');
  const [commercial_registration_document, setCommercial_registration_document] = useState<any>('');
  // Freelancer
  const [certificate_number_document, setCertificate_number_document] = useState<any>('');
  const [tax_number_document, setTax_number_document] = useState<any>('');
  const { formatMessage, locale } = useIntl();
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const params: any = useParams();
  const [mainLightLogo, setmainLightLogo] = useState<Blob | string>('');
  const [mainDarkLogo, setMainDarkLogo] = useState<Blob | string>('');
  const [mainLightMarkLogo, setMainLogoMarkLight] = useState<Blob | string>('');
  const [mainDarkMarkLogo, setMainDarkMarkLogo] = useState<Blob | string>('');
  const [originaleData, setOriginaleData] = useState<any>({});
  const [showWarningsConfirmationModal, setShowWarningsConfirmationModal] = useState(false);
  const [warningsConfirmation, setWarningsConfirmation] = useState<any>('');
  const [showCategoriesWarning, setShowCategoriesWarning] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);

  const [categoriesByActivitiesList, setCategoriesByActivitiesList] = useState<any>(null);

  const [salesInvoiceNumberStartDisabled, setSalesInvoiceNumberStartDisabled] = useState(true);
  const [returnSalesInvoiceNumberStartDisabled, setReturnSalesInvoiceNumberStartDisabled] =
    useState(true);
  /* ------------------------- On INIT ------------------------ */

  const formik = useFormik({
    initialValues: { ...initialValues },
    validationSchema: schema,
    onSubmit: async (values, { setSubmitting }) => {
      setLoading(true);
      try {
        if (
          [tax_number_document, certificate_number_document, commercial_registration_document].some(
            //
            (file) => {
              return file?.size ? file.size / 1024 / 1024 > fileMaxSize : false;
            }
          )
        ) {
          toast.error(
            formatMessage(
              { id: 'The maximum file size is {maxSize}MB' },
              { maxSize: `${fileMaxSize}` }
            )
          );
          setLoading(false);
          setSubmitting(false);
          return;
        }

        const payload: any = {
          ...values,
          type: values.type.value,
          certificate_number_document,
          commercial_registration_document,
          bank_information: {
            account_name: values.account_name,
            bank_name: values.bank_name,
            account_number: values.account_number,
            iban: values.iban,
          },
          // tax_document: tax_number_document,
        };
        payload['period_type'] = values.report_period[0].value;
        payload['vat_registeration_number'] = values.vat_registeration_number;
        payload['tax_registration_date'] = values.tax_registration_date;
        payload['categories'] = [
          ...categoriesByActivitiesList?.sales.filter((category: any) => {
            return values.sales_categories.includes(`${category.id}`) || category.is_mandatory;
          }),
          ...categoriesByActivitiesList?.purchases.filter((category: any) => {
            return values.purchases_categories.includes(`${category.id}`) || category.is_mandatory;
          }),
        ].map((category: any) => {
          return category.id;
        });
        payload['country'] = values.country;
        payload['city'] = values.city;
        payload['zip'] = values.zip;
        payload['address'] = {};
        payload['address']['ar'] = values.address_ar;
        payload['address']['en'] = values.address_en;
        payload['default_vat_enabled'] = values.default_vat_enabled;
        payload['vat_registered'] = values.vat_registered;

        delete payload.report_period;
        delete payload.sales_categories;
        delete payload.purchases_categories;
        delete payload.account_name;
        delete payload.bank_name;
        delete payload.account_number;
        delete payload.iban;

        const formData = serialize(payload);
        formData.set('logo_light', mainLightLogo);
        formData.set('logo_dark', mainDarkLogo);
        formData.set('logo_light_mark', mainLightMarkLogo);
        formData.set('logo_dark_mark', mainDarkMarkLogo);

        if (typeof certificate_number_document !== 'string')
          formData.append('certificate_number_document', certificate_number_document);
        if (typeof tax_number_document !== 'string')
          formData.append('tax_number_document', tax_number_document);
        if (typeof commercial_registration_document !== 'string')
          formData.append('commercial_registration_document', commercial_registration_document);

        await axiosInstance.post(`/companies/${companyDetails.id}/profile`, formData);

        dispatch(CompaniesRedux.actions.getCurrentCompanyById({ id: companyDetails.id }));

        toast.success(
          formatMessage({ id: 'Your company settings has been updated successfully!' })
        );

        // dispatch(
        //   CompaniesRedux.actions.getUserCompanies({
        //     params: {
        //       per_page: 100,
        //     },
        //   })
        // );
        toast.success(formatMessage({ id: 'An admin will review your information' }));

        formik.resetForm();
        formik.setTouched({});
        history.push(`/c/${params.companySlug}/settings/companydetails`);
      } catch (error: any) {
        DisplayErrors(error);
        setSubmitting(false);
      }
      setLoading(false);
    },
  });

  const { values, touched, errors } = formik;

  const CompanyTypesOptions: SelectOptions = useMemo(
    () => [
      { value: CompanyTypes.Freelancer, label: formatMessage({ id: 'Freelancer' }) },
      { value: CompanyTypes.Corporate, label: formatMessage({ id: 'Corporate' }) },
    ],
    [formatMessage]
  );

  // const companies: Array<CompanyModel> = useSelector<RootState>(
  //   (state) => state?.USER_companies.companies,
  //   shallowEqual
  // ) as Array<CompanyModel>;

  const companyDetails: ICompany = useSelector<RootState>(
    (state) => state?.USER_companies.companyDetails,
    shallowEqual
  ) as ICompany;

  const reportPeriodOptions = useMemo(() => {
    return Array.from(ReportsPeriodTypeMap.values())
      .flat()
      .map((option) => toSelectOption(option, locale));
  }, [locale]);
  /* ------------------------- Get companies activitiess  ------------------------ */

  const { isLoading: categoriesActivitiesLoading, data: categoriesActivitiesList } = useQuery(
    'companyDetailsActivities',
    () =>
      axiosInstance(`/activities`, {
        params: {
          per_page: 0,
        },
      }).then((res) => {
        return res.data;
      }),
    { retryDelay: 300, retry: 3 }
  );

  /* -------------------------end Get companies activitiess  ------------------------ */

  /* ------------------------- Get categories by activity ------------------------ */

  const { isLoading: categoriesByActivitiesLoading, data: categoriesByActivitiesListData } =
    useQuery(
      'companyDetailsCategoriesByActivitiesList',
      () =>
        axiosInstance(`/categories/by-activities`, {
          params: {
            // type: `1`,
            company_activities: values?.company_activities?.map((activity: any) =>
              parseInt(activity)
            ),
            per_page: 0,
          },
        }).then((res) => {
          return res.data;
        }),
      {
        retryDelay: 300,
        retry: 3,
        enabled: values?.company_activities?.length > 0,
      }
    );

  useEffect(() => {
    if (!categoriesByActivitiesListData) return;
    else setCategoriesByActivitiesList(categoriesByActivitiesListData);
  }, [categoriesByActivitiesListData]);

  /* -------------------------end Get categories by activity  ------------------------ */

  useEffect(() => {
    formik.setFieldValue(
      'type',
      CompanyTypesOptions.find((type) => type.value === CompanyTypes[companyDetails.type])
    );
    formik.setFieldValue('slug', companyDetails.slug);
    formik.setFieldValue('name', companyDetails.name || '');
    formik.setFieldValue('website', companyDetails.website || '');
    formik.setFieldValue('commercial_registration', companyDetails.commercial_registration || '');
    formik.setFieldValue('certificate_number', companyDetails.certificate_number || '');
    formik.setFieldValue('unified_number', companyDetails.unified_number || '');
    formik.setFieldValue('national_id', companyDetails.national_id || '');

    setCommercial_registration_document(companyDetails.commercial_registration_document);
    setCertificate_number_document(companyDetails.certificate_number_document);
    setOriginaleData(companyDetails);
    if (companyDetails?.branding) {
      const branding: any = companyDetails.branding;
      setmainLightLogo(branding.logo.light);
      setMainDarkLogo(branding.logo.dark);
      setMainLogoMarkLight(branding.mark.light);
      setMainDarkMarkLogo(branding.mark.dark);
    }
  }, [companyDetails]);
  // set intial values for formik
  useEffect(() => {
    formik.setFieldValue(
      `report_period`,
      reportPeriodOptions?.filter((option) => {
        if (companyDetails.settings?.period_type) {
          return option.value === companyDetails.settings?.period_type;
        } else {
          return option.value === 3;
        }
      })
    );
    formik.setFieldValue(
      `vat_registeration_number`,
      companyDetails.vat_registeration_number ? companyDetails.vat_registeration_number : ''
    );

    formik.setFieldValue(
      'address_ar',
      companyDetails?.address?.ar ? companyDetails.address.ar : initialValues.address_ar
    );
    formik.setFieldValue(
      'address_en',
      companyDetails?.address?.en ? companyDetails.address.en : initialValues.address_en
    );
    formik.setFieldValue('zip', companyDetails?.zip ? companyDetails.zip : initialValues.zip);
    formik.setFieldValue('city', companyDetails.city ? companyDetails.city : initialValues.city);
    const country = countires?.find(
      (country: any) => companyDetails?.country?.value === country?.iso
    );

    formik.setFieldValue(
      'country',
      companyDetails.country
        ? country
          ? { label: country.emoji + ' ' + country.name[locale], value: country.iso }
          : null
        : initialValues.country
    );
    formik.setFieldValue(
      `tax_registration_date`,
      companyDetails.settings?.tax_registration_date
        ? companyDetails.settings?.tax_registration_date
        : ''
    );
    formik.setFieldValue(
      `company_activities`,
      companyDetails.company_activities?.map((activity) => {
        return `${activity.id}`;
      })
    );
    formik.setFieldValue(
      `sales_categories`,
      companyDetails.categories?.sales?.map((category) => {
        return `${category.id}`;
      })
    );
    formik.setFieldValue(
      `purchases_categories`,
      companyDetails.categories?.purchases?.map((category) => {
        return `${category.id}`;
      })
    );
    formik.setFieldValue('tax_number_document', companyDetails.tax_number_document || '');
    formik.setFieldValue(
      'default_vat_enabled',
      companyDetails?.settings?.default_vat_enabled ? '1' : '0'
    );
    formik.setFieldValue('vat_registered', companyDetails?.settings?.vat_registered ? '1' : '0');

    formik.setFieldValue(
      'invoice_number_method',
      companyDetails?.settings?.invoice_number_method?.toString()
    );
    formik.setFieldValue(
      'sales_invoice_number_prefix',
      companyDetails?.settings?.sales_invoice_number_prefix
        ? companyDetails?.settings?.sales_invoice_number_prefix
        : ''
    );
    formik.setFieldValue(
      'sales_invoice_number_start',
      companyDetails?.settings?.sales_invoice_number_start
        ? companyDetails?.settings?.sales_invoice_number_start
        : ''
    );
    formik.setFieldValue(
      'return_sales_invoice_number_prefix',
      companyDetails?.settings?.return_sales_invoice_number_prefix
        ? companyDetails?.settings?.return_sales_invoice_number_prefix
        : ''
    );
    formik.setFieldValue(
      'return_sales_invoice_number_start',
      companyDetails?.settings?.return_sales_invoice_number_start
        ? companyDetails?.settings?.return_sales_invoice_number_start
        : ''
    );

    setTax_number_document(companyDetails.tax_number_document);
    // setOriginaleData(companyDetails);
    //  delete payload.account_name;
    //  delete payload.bank_name;
    //  delete payload.account_number;
    //  delete payload.iban;
    formik.setFieldValue('account_name', companyDetails?.bank_information?.account_name || '');
    formik.setFieldValue('bank_name', companyDetails?.bank_information?.bank_name || '');
    formik.setFieldValue('account_number', companyDetails?.bank_information?.account_number || '');
    formik.setFieldValue('iban', companyDetails?.bank_information?.iban || '');
  }, [companyDetails]);

  const images = [
    {
      image: mainLightLogo,
      title: 'Upload a Company Logo- Light Style',
      setFunction: (f: Blob) => setmainLightLogo(f),
    },
    {
      image: mainDarkLogo,
      title: 'Upload a Company Logo- Dark Style',
      setFunction: (f: Blob) => setMainDarkLogo(f),
    },
    {
      image: mainLightMarkLogo,
      title: 'Upload a Company Logo- Light Mark',
      setFunction: (f: Blob) => setMainLogoMarkLight(f),
    },
    {
      image: mainDarkMarkLogo,
      title: 'Upload a Company Logo- Dark Mark',
      setFunction: (f: Blob) => setMainDarkMarkLogo(f),
    },
  ];

  const handleSelectedActivities = (activitiesFetchedData: any) => {
    if (!activitiesFetchedData || !categoriesByActivitiesList) return;

    if (
      JSON.stringify(values?.company_activities) !==
      JSON.stringify(
        companyDetails.company_activities?.map((activity) => {
          return `${activity.id}`;
        })
      )
    ) {
      setCategoriesByActivitiesList(activitiesFetchedData);

      if (activitiesFetchedData?.sales?.length > 0) {
        let selectedSalesCategories = activitiesFetchedData?.sales
          .filter((category: any) => {
            return category.is_selected;
          })
          .map((category: any) => {
            return `${category?.id}`;
          })
          .concat(
            companyDetails.categories?.sales?.map((category) => {
              return `${category.id}`;
            })
          );

        if (values?.company_activities?.length > 0) {
          formik.setFieldValue(
            'sales_categories',
            Array.from(new Set([...selectedSalesCategories]))
          );
        }
      }

      if (activitiesFetchedData?.purchases?.length > 0) {
        let selectedPurchasesCategories = activitiesFetchedData?.purchases
          .filter((category: any) => {
            return category.is_selected;
          })
          .map((category: any) => {
            return `${category?.id}`;
          })
          .concat(
            companyDetails.categories?.purchases?.map((category) => {
              return `${category.id}`;
            })
          );

        if (values?.company_activities?.length > 0) {
          formik.setFieldValue(
            'purchases_categories',
            Array.from(new Set([...selectedPurchasesCategories]))
          );
        }
      }
    } else {
      formik.setFieldValue(
        `sales_categories`,
        companyDetails.categories?.sales?.map((category) => {
          return `${category.id}`;
        })
      );
      formik.setFieldValue(
        `purchases_categories`,
        companyDetails.categories?.purchases?.map((category) => {
          return `${category.id}`;
        })
      );
    }
  };

  useEffect(() => {
    axiosInstance(`/categories/by-activities`, {
      params: {
        company_activities: values?.company_activities?.map((activity: any) => parseInt(activity)),
        per_page: 0,
      },
    }).then((res) => {
      if (!res.data) return;
      else handleSelectedActivities(res.data);
    });
  }, [values?.company_activities]);

  // reset to company values if user click on on change from manual to sequential
  useEffect(() => {
    if (values?.invoice_number_method === '1') {
      formik.setFieldValue(
        'sales_invoice_number_prefix',
        companyDetails?.settings?.sales_invoice_number_prefix
          ? companyDetails?.settings?.sales_invoice_number_prefix
          : ''
      );
      formik.setFieldValue(
        'sales_invoice_number_start',
        companyDetails?.settings?.sales_invoice_number_start
          ? companyDetails?.settings?.sales_invoice_number_start
          : ''
      );
      formik.setFieldValue(
        'return_sales_invoice_number_prefix',
        companyDetails?.settings?.return_sales_invoice_number_prefix
          ? companyDetails?.settings?.return_sales_invoice_number_prefix
          : ''
      );
      formik.setFieldValue(
        'return_sales_invoice_number_start',
        companyDetails?.settings?.return_sales_invoice_number_start
          ? companyDetails?.settings?.return_sales_invoice_number_start
          : ''
      );
      setSalesInvoiceNumberStartDisabled(true);
      setReturnSalesInvoiceNumberStartDisabled(true);
    }
  }, [values?.invoice_number_method]);

  const shouldAsk = () =>
    originaleData.type &&
    (Object.keys(touched).length > 0 ||
      certificate_number_document !== originaleData.certificate_number_document ||
      tax_number_document !== originaleData.tax_number_document ||
      commercial_registration_document !== originaleData.commercial_registration_document);

  useEffect(() => {
    setShowExitPrompt(shouldAsk());
  }, [formik]);

  useEffect(() => {
    return () => {
      setDisable();
      setShowExitPrompt(false);
    };
  }, []);

  return (
    <>
      {/* //TODO: add edit and separate view */}
      {/* {shouldAsk() && <Prompt message={$t('Are you sure you want to ignore the changes?')} />} */}
      <div className='d-flex px-5 '>
        <FormikProvider value={formik}>
          <Form
            id='company-settings-completion'
            className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework p-lg-16 pb-lg-10 p-md-10'
            data-testid='complete-settings-form'
            noValidate
            autoComplete='none'
            style={{ width: '100%' }}
          >
            {Object.keys(formik.touched).length > 0 && (
              <Prompt message={$t('Are you sure you want to ignore the changes?')} />
            )}
            {displayRequiredSection === 1 && (
              <>
                <div className='p-10 ' style={{ width: '100%' }}>
                  <div className='row'>
                    <div className='col-md-6'>
                      <Input
                        placeholder={formatMessage({ id: 'Company short name / code' })}
                        autoComplete='off'
                        labelId={formatMessage({ id: 'Company short name / code' })}
                        formik={formik}
                        name='slug'
                        disabled
                      />
                    </div>
                    <div className='col-md-6'>
                      <Input
                        placeholder={formatMessage({ id: 'Company Full Name' })}
                        autoComplete='off'
                        labelId={formatMessage({ id: 'Company Full Name' })}
                        formik={formik}
                        required
                        name='name'
                        setTouchedOnInput
                        disabled={userCanNot('update-company') || mode === 'view'}
                      />
                    </div>
                    {/*  --------------------- Country -----------------------  */}
                    <div className='col-md-6 mb-3'>
                      <Label msg='Country' htmlFor='country-select' isRequired={false} />
                      <Select
                        placeholder={$t('Country')}
                        className='react-select smaller'
                        id='country-select'
                        value={formik.values.country}
                        onChange={(value) => {
                          formik.setFieldValue('country', value);
                          formik.setFieldValue('city', null);
                          formik.setFieldValue('zip', null);
                        }}
                        options={countires?.map((country: any) => ({
                          value: country.iso,
                          label: country?.emoji + ' ' + country?.name?.[locale],
                        }))}
                        isDisabled={mode === 'view'}
                      />
                    </div>
                    {/*  --------------------- end Country -----------------------  */}
                    {/* ------------------------------ city options ------------------------------ */}
                    <div
                      className='col-md-6'
                      style={{
                        zIndex: 99,
                      }}
                    >
                      <Label msg='City' htmlFor='city-select' />
                      <Select
                        isDisabled={mode === 'view' || !values?.country?.value?.length}
                        placeholder={$t('City')}
                        className='react-select smaller'
                        onChange={(value) => formik.setFieldValue('city', value)}
                        onBlur={() => formik.setFieldTouched('city', true)}
                        value={formik.values.city}
                        id='city-select'
                        options={countires
                          .find((country: any) => country.iso === values?.country?.value)
                          ?.cities?.map((city: any) => ({
                            value: city.iso.toString(),
                            label: city?.name?.[locale],
                          }))}
                      />

                      {/* error message  */}
                      {errors?.city && formik.touched.city && (
                        <div className='fv-plugins-message-container mt-2 mx-3 text-danger'>
                          <span role='alert' className='text-danger'>
                            <FormattedMessage id={'This field is required'} />
                          </span>
                        </div>
                      )}
                    </div>
                    {/* ---------------------------- end city options ---------------------------- */}
                    <div className='col-md-6'>
                      <Input
                        setTouchedOnInput
                        placeholder={$t('Address Information') + $t(' (AR)')}
                        labelId={$t('Address Information') + $t(' (AR)')}
                        formik={formik}
                        name='address_ar'
                        disabled={mode === 'view'}
                      />
                    </div>
                    <div className='col-md-6'>
                      <Input
                        setTouchedOnInput
                        placeholder={$t('Address Information') + $t(' (EN)')}
                        labelId={$t('Address Information') + $t(' (EN)')}
                        formik={formik}
                        name='address_en'
                        disabled={mode === 'view'}
                      />
                    </div>
                    <div className='col-md-6'>
                      <Input
                        setTouchedOnInput
                        placeholder={$t('Zip Code')}
                        labelId={$t('Zip Code')}
                        formik={formik}
                        name='zip'
                        disabled={mode === 'view'}
                      />
                    </div>
                    {companyDetails?.type && formik?.values?.type?.value && (
                      <div className='col-md-6'>
                        <label
                          className={clsx('form-label fs-6 my-1 fw-bolder text-dark required')}
                          htmlFor={'company_type'}
                        >
                          <FormattedMessage id='Company type' />
                        </label>
                        <Select
                          onChange={(value) => {
                            formik.setFieldValue('type', value);
                            if (value?.value === CompanyTypes.Corporate) {
                              formik.setFieldValue('certificate_number', '');
                            } else {
                              formik.setTouched({ ...formik.touched, website: false });
                              // formik.setFieldValue('tax_number', '');
                              formik.setFieldValue('commercial_registration', '');
                            }
                          }}
                          onBlur={() => formik.setFieldTouched('type', true)}
                          options={CompanyTypesOptions}
                          value={formik.values.type}
                          className='react-select smaller'
                          id='company_type'
                          isDisabled={userCanNot('update-company') || mode === 'view'}
                        />
                      </div>
                    )}
                    <div className='col-md-6'>
                      <label
                        className={clsx('form-label fs-6 my-1 fw-bolder text-dark')} /* , {
                required: values?.type?.['value'] === CompanyTypes.Corporate,
              } */
                        htmlFor={'website'}
                      >
                        <FormattedMessage id='Website' />
                      </label>
                      <div className='website-field d-flex' dir='ltr'>
                        {!values?.website?.startsWith('http:') &&
                          !values?.website?.startsWith('www.') && (
                            <span className='text-lightGreyBlue mt-3 mx-1'>www.</span>
                          )}
                        <div className='flex-fill' dir={locale === 'ar' ? 'rtl' : 'ltr'}>
                          <Input
                            id='website'
                            // required
                            placeholder={formatMessage({ id: 'Website' })}
                            autoComplete='off'
                            formik={formik}
                            type='website'
                            name='website'
                            setTouchedOnInput
                            disabled={userCanNot('update-company') || mode === 'view'}
                          />
                        </div>
                      </div>
                    </div>

                    {values?.type?.value === CompanyTypes.Freelancer && (
                      <>
                        <div className='col-md-6'>
                          <Input
                            placeholder={formatMessage({ id: 'Freelancer Certificate Number' })}
                            labelId={formatMessage({ id: 'Freelancer Certificate Number' })}
                            formik={formik}
                            name='certificate_number'
                            setTouchedOnInput
                            disabled={userCanNot('update-company') || mode === 'view'}
                          />
                        </div>
                        <div className='col-md-6'>
                          <Input
                            placeholder={formatMessage({ id: 'National ID' })}
                            labelId={formatMessage({ id: 'National ID' })}
                            formik={formik}
                            name='national_id'
                            setTouchedOnInput
                            disabled={userCanNot('update-company') || mode === 'view'}
                          />
                        </div>

                        <div className='col-md-6'>
                          <FileUploader
                            setFile={setCertificate_number_document}
                            file={certificate_number_document}
                            maxSize={2048}
                            supportedFiles={allowedFiles}
                            typesOnly='pdfs-images'
                            disabled={userCanNot('update-company') || mode === 'view'}
                          />
                        </div>
                      </>
                    )}
                    {values?.type?.value === CompanyTypes.Corporate && (
                      <>
                        <div className='col-md-6'>
                          <Input
                            placeholder={formatMessage({
                              id: 'Company Commercial Registration Number',
                            })}
                            autoComplete='off'
                            labelId={formatMessage({
                              id: 'Company Commercial Registration Number',
                            })}
                            formik={formik}
                            name='commercial_registration'
                            setTouchedOnInput
                            disabled={userCanNot('update-company') || mode === 'view'}
                          />
                        </div>

                        <div className='col-md-6 '>
                          <Input
                            placeholder={formatMessage({ id: 'Unified Number' })}
                            autoComplete='off'
                            labelId={formatMessage({ id: 'Unified Number' })}
                            formik={formik}
                            name='unified_number'
                            setTouchedOnInput
                            disabled={userCanNot('update-company') || mode === 'view'}
                          />
                        </div>

                        <div className='col-md-6'>
                          <FileUploader
                            setFile={setCommercial_registration_document}
                            file={commercial_registration_document}
                            maxSize={2048}
                            supportedFiles={allowedFiles}
                            disabled={userCanNot('update-company') || mode === 'view'}
                            typesOnly='pdfs-images'
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
            {displayRequiredSection === 2 && (
              <>
                <div className='pt-10 px-10'>
                  <div className={clsx('contianer gap-10 jusify-content-center')}>
                    <div className='row'>
                      {images.map((image, i) => (
                        <div
                          className='col-xs-12 col-md-8 col-lg-6 col-xl-5'
                          style={{ maxWidth: 'fit-content' }}
                        >
                          <LogoUploader
                            key={image.title}
                            title={image.title}
                            width={300}
                            setFunction={(file: any) => image.setFunction(file)}
                            maxSizeInBytes={maxFileSizeInMegaBytes * 1024}
                            image={image.image}
                            isDisabled={mode === 'view' ? false : true}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div
                    className='bg-paleGrey text-blueGrey h-50px d-flex align-items-center p-4 mt-10 mb-10'
                    style={{ borderRadius: 10 }}
                  >
                    <ul className='d-flex gap-5 pt-4'>
                      <li className='d-inline'>
                        <div className='d-flex'>
                          <div className='bg-lightGreyBlue rounded-circle border border-5 h-20px w-20px'></div>
                          <FormattedMessage
                            id='Max file size : {size} MB'
                            values={{ size: maxFileSizeInMegaBytes }}
                          />
                        </div>
                      </li>
                      <li className='d-inline mx-5'>
                        <div className='d-flex'>
                          <div className='bg-lightGreyBlue rounded-circle border border-5 h-20px w-20px'></div>
                          <FormattedMessage id='File format : (  PNG, JPG and SVG )' />
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </>
            )}
            {displayRequiredSection === 3 && (
              <>
                {/*  ---------------------  TAX registered -----------------------  */}

                <div className='row my-10'>
                  <label
                    className='d-flex flex-column mb-0 cursor-pointer'
                    title={formatMessage({
                      id: 'TAX registered',
                    })}
                  >
                    <span className='d-flex align-items-center m-2'>
                      <span className='form-switch form-switch-custom form-switch-solid me-3'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          role='switch'
                          name='vat_registered'
                          checked={values?.vat_registered === '1' ? true : false}
                          onChange={(event: any) => {
                            formik.setFieldValue(
                              'vat_registered',
                              event.target.checked ? '1' : '0'
                            );
                          }}
                          disabled={mode === 'view'}
                          id='vat_registered'
                        />
                      </span>

                      <span className='d-flex flex-column'>
                        <span className='fw-bolder text-gray-800 text-hover-primary fs-5'>
                          <FormattedMessage id='TAX registered' />
                        </span>
                      </span>
                    </span>
                  </label>
                </div>
                {/*  --------------------- end TAX registered -----------------------  */}

                {values?.vat_registered === '1' && (
                  <div className='row mb-10'>
                    {/*  ---------------------  Report type select-----------------------  */}
                    <div className='col-6'>
                      <Label msg='Report type' htmlFor='report_period' isRequired />
                      <Select
                        onChange={(value) => formik.setFieldValue('report_period', [value])}
                        onBlur={() => formik.setFieldTouched('report_period', true)}
                        options={reportPeriodOptions?.filter(
                          (option) => formik.values.report_period[0]?.value !== option.value
                        )}
                        value={formik.values.report_period}
                        placeholder={$t('Report type')}
                        isDisabled={mode === 'view'}
                        className='react-select smaller'
                        id='report_period'
                      />
                      <ErrorMessage name='report_period' formik={formik} />
                    </div>
                    {/*  --------------------- end Report type select-----------------------  */}
                    <div className='col-md-6'>
                      <Input
                        placeholder={formatMessage({ id: 'VAT registration number' })}
                        autoComplete='off'
                        labelId={formatMessage({ id: 'VAT registration number' })}
                        formik={formik}
                        name={'vat_registeration_number'}
                        disabled={userCanNot('update-company') || mode === 'view'}
                        setTouchedOnInput
                        // required
                        type={'text'}
                      />
                    </div>
                    {/*  ------------------------ Tax Registration Date -----------------------  */}
                    <div className='col-md-6'>
                      <div className='row'>
                        <label
                          className={'form-label fs-6 my-1 fw-bolder text-dark'} // required
                          htmlFor='tax_registration_date_id'
                        >
                          {formatMessage({
                            id: 'Tax registration date',
                          })}
                        </label>
                        <DatePicker
                          isClearable={mode !== 'view'}
                          selected={
                            formik.values.tax_registration_date
                              ? new Date(formik.values.tax_registration_date)
                              : null
                          }
                          dateFormat='dd/MM/yyyy'
                          placeholderText={formatMessage({
                            id: 'Tax registration date',
                          })}
                          id='tax_registration_date_id'
                          className='form-control form-control-md b-radius-16 basic-input '
                          onChange={(date: any) =>
                            formik.setFieldValue(`tax_registration_date`, date ? date : '')
                          }
                          maxDate={new Date()}
                          onChangeRaw={(e) => {
                            formik.setFieldTouched(`tax_registration_date`, true, true);
                          }}
                          onKeyDown={(e) => {
                            e.preventDefault();
                          }}
                          autoComplete='off'
                          disabled={mode === 'view'}
                        />
                        <ErrorMessage name='tax_registration_date' formik={formik} />
                      </div>
                      {/*  --------------------- end Tax Registration Date-----------------------  */}

                      {/*  ---------------------  Default vat rate -----------------------  */}

                      <div className='row mt-8'>
                        <label
                          className='d-flex flex-column mb-0 cursor-pointer'
                          title={formatMessage({
                            id: 'TAX rate auto assign in case of invoice import',
                          })}
                        >
                          <span className='d-flex align-items-center m-2'>
                            <span className='form-switch form-switch-custom form-switch-solid me-3'>
                              <input
                                className='form-check-input'
                                type='checkbox'
                                role='switch'
                                name='default_vat_enabled'
                                checked={values?.default_vat_enabled === '1' ? true : false}
                                onChange={(event: any) => {
                                  formik.setFieldValue(
                                    'default_vat_enabled',
                                    event.target.checked ? '1' : '0'
                                  );
                                }}
                                disabled={mode === 'view'}
                                id='default_vat_enabled'
                              />
                            </span>

                            <span className='d-flex flex-column'>
                              <span className='fw-bolder text-gray-800 text-hover-primary fs-5'>
                                <FormattedMessage id='TAX rates auto assign' />
                              </span>
                            </span>
                          </span>
                        </label>
                      </div>
                      {/*  --------------------- end Default vat rate -----------------------  */}
                    </div>

                    <div className='col-md-6 mt-5'>
                      <FileUploader
                        setFile={setTax_number_document}
                        file={tax_number_document}
                        maxSize={2048}
                        supportedFiles={allowedFiles}
                        disabled={userCanNot('update-company') || mode === 'view'}
                        typesOnly='pdfs-images'
                      />
                    </div>
                    <div className='col-md-6 '></div>
                  </div>
                )}
              </>
            )}
            {displayRequiredSection === 4 && (
              <>
                <div className='row mb-10'>
                  {/*  ---------------------  select activities-----------------------  */}

                  <div className='col-12'>
                    <Label msg='Company activities' htmlFor='company_activity' isRequired />
                    <div
                      role='group'
                      aria-labelledby='checkbox-group'
                      className='my-3'
                      id='company_activity'
                    >
                      {!categoriesActivitiesLoading &&
                        categoriesActivitiesList &&
                        categoriesActivitiesList?.length > 0 &&
                        categoriesActivitiesList?.map((activity: any) => (
                          <>
                            <div className='form-check form-check-custom form-check-solid '>
                              {' '}
                              <label className='form-check form-switch form-check-custom form-check-solid'>
                                {/*  tax registered */}
                                <Field
                                  key={activity.id}
                                  type='checkbox'
                                  className='form-check-box'
                                  title={activity.name}
                                  // onChange={() => {
                                  //   if (
                                  //     !formik.values.company_activities?.includes(`${activity.id}`)
                                  //   ) {
                                  //     formik.setFieldValue(
                                  //       'company_activities',
                                  //       formik.values.company_activities?.concat(`${activity.id}`)
                                  //     );
                                  //   } else {
                                  //     formik.setFieldValue(
                                  //       'company_activities',
                                  //       formik.values.company_activities?.filter(
                                  //         (item: any) => item !== `${activity.id}`
                                  //       )
                                  //     );
                                  //   }
                                  // }}
                                  value={`${activity.id}`}
                                  name='company_activities'
                                  checked={formik.values.company_activities?.includes(
                                    `${activity.id}`
                                  )}
                                  disabled={mode === 'view'}
                                  id={activity.id}
                                />
                                <span className='form-check-label fw-bold text-black-400'>
                                  {activity.name}
                                </span>
                              </label>
                            </div>
                          </>
                        ))}
                      <ErrorMessage name='company_activities' formik={formik} />
                    </div>
                  </div>

                  {/*  ---------------------  end select activities-----------------------  */}
                  {/*  ---------------------  select categories-----------------------  */}

                  {formik?.values?.company_activities && categoriesByActivitiesList?.sales && (
                    <div className='col-6'>
                      <Label msg='Sales categories' htmlFor='sales_category' isRequired />
                      <div role='group' aria-labelledby='checkbox-group' className='mt-3'>
                        {!categoriesByActivitiesLoading &&
                          categoriesByActivitiesList?.sales.length > 0 &&
                          categoriesByActivitiesList?.sales?.map((category: any) => (
                            <>
                              <div className='form-check form-check-custom form-check-solid '>
                                {' '}
                                <label className='form-check form-switch form-check-custom form-check-solid'>
                                  {/*  tax registered */}
                                  <Field
                                    key={category.id}
                                    type='checkbox'
                                    className='form-check-box'
                                    title={category.name}
                                    onChange={() => {
                                      if (
                                        !formik.values.sales_categories?.includes(`${category.id}`)
                                      ) {
                                        if (category?.has_warning) {
                                          setShowCategoriesWarning(true);
                                          setSelectedCategory(category);
                                        } else {
                                          formik.setFieldValue(
                                            'sales_categories',
                                            formik.values.sales_categories?.concat(`${category.id}`)
                                          );
                                        }
                                      } else {
                                        formik.setFieldValue(
                                          'sales_categories',
                                          formik.values.sales_categories?.filter(
                                            (item: any) => item !== `${category.id}`
                                          )
                                        );
                                      }
                                    }}
                                    // value={`${category.id}`}
                                    name='sales_categories'
                                    checked={
                                      formik.values.sales_categories?.includes(`${category.id}`) ||
                                      category?.is_mandatory
                                    }
                                    disabled={mode === 'view' || category?.is_mandatory}
                                    id={category.id}
                                  />
                                  <span className='form-check-label fw-bold text-black-400'>
                                    {category.name}
                                  </span>
                                </label>
                              </div>
                            </>
                          ))}
                        <ErrorMessage name='sales_categories' formik={formik} />
                      </div>
                    </div>
                  )}
                  {formik?.values?.company_activities && categoriesByActivitiesList?.purchases && (
                    <div className='col-6'>
                      <Label msg='Purchases categories' htmlFor='purchases_category' isRequired />
                      <div role='group' aria-labelledby='checkbox-group' className='mt-3'>
                        {!categoriesByActivitiesLoading &&
                          categoriesByActivitiesList?.purchases.length > 0 &&
                          categoriesByActivitiesList?.purchases?.map((category: any) => (
                            <>
                              <div className='form-check form-check-custom form-check-solid '>
                                {' '}
                                <label className='form-check form-switch form-check-custom form-check-solid'>
                                  {/*  tax registered */}
                                  <Field
                                    key={category.id}
                                    type='checkbox'
                                    className='form-check-box'
                                    title={category.name}
                                    value={`${category.id}`}
                                    name='purchases_categories'
                                    checked={
                                      formik.values.purchases_categories?.includes(
                                        `${category.id}`
                                      ) || category?.is_mandatory
                                    }
                                    disabled={mode === 'view' || category?.is_mandatory}
                                    id={category.id}
                                  />
                                  <span className='form-check-label fw-bold text-black-400'>
                                    {category.name}
                                  </span>
                                </label>
                              </div>
                            </>
                          ))}
                        <ErrorMessage name='purchases_categories' formik={formik} />
                      </div>
                    </div>
                  )}

                  {/*  --------------------- end select categories-----------------------  */}
                </div>
              </>
            )}
            {displayRequiredSection === 5 && (
              <>
                <div className='row mb-10'>
                  <div className='m-10'>
                    <div className='row fv-row'>
                      <label className='d-flex flex-column mb-0 cursor-pointer'>
                        <span className='d-flex align-items-center m-2'>
                          <span className='form-switch form-switch-custom form-switch-solid me-3'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              role='switch'
                              name='invoice_number_method'
                              checked={values?.invoice_number_method === '2' ? true : false}
                              onChange={(event: any) => {
                                formik.setFieldValue(
                                  'invoice_number_method',
                                  event.target.checked ? '2' : '1'
                                );
                              }}
                              disabled={mode === 'view' || userCanNot('e_invoicing-company')}
                              id='invoice_number_method'
                            />
                          </span>

                          <span className='d-flex flex-column'>
                            <span className='fw-bolder text-gray-800 text-hover-primary fs-5'>
                              {values?.invoice_number_method === '1' && (
                                <FormattedMessage id='Manual: invoices will be generated through system with an invoice number entered manually for invoice that are created or uploaded from excel sheet.' />
                              )}
                              {values?.invoice_number_method === '2' && (
                                <FormattedMessage id='Sequential: invoices will be generated through the system with a sequential invoice number that is auto-generated for each issued invoice that can only be created through the system.' />
                              )}
                            </span>
                          </span>
                        </span>
                      </label>
                    </div>
                    {userCanNot('e_invoicing-company') && (
                      <>
                        <span className='d-flex flex-column'>
                          <span className='fw-bolder text-gray-800 text-hover-primary fs-5 m-5'>
                            {
                              <FormattedMessage id='To enable E-invoicing module, please contact support.' />
                            }
                          </span>
                        </span>
                      </>
                    )}
                    {values?.invoice_number_method === '2' && userCan('e_invoicing-company') && (
                      <>
                        <div className='content-wrapper bg-white b-radius-25 mt-10'>
                          <div className='d-flex px-5 '>
                            <div
                              className='d-flex justify-content-center px-10 py-3 fs-6 '
                              style={{ width: '100%' }}
                            >
                              <FormattedMessage id='Sales invoices' />
                            </div>
                          </div>
                          <SeparatorLines lines={2} />
                          <div className='row fv-row m-10 '>
                            <div className='col-md-6'>
                              <Input
                                placeholder={$t('Prefix code')}
                                autoComplete='off'
                                labelId={$t('Prefix code')}
                                name={`sales_invoice_number_prefix`}
                                disabled={mode === 'view'}
                                type='text'
                                formik={formik}
                                required
                                onChange={(e: any) => {
                                  formik.setFieldValue(
                                    'sales_invoice_number_prefix',
                                    `${e.target.value}`
                                  );
                                  setSalesInvoiceNumberStartDisabled(false);
                                }}
                              />
                            </div>
                            <div className='col-md-6'>
                              <Input
                                placeholder={$t('Invoices start number')}
                                autoComplete='off'
                                labelId={$t('Invoices start number')}
                                name={`sales_invoice_number_start`}
                                disabled={
                                  mode === 'view' ||
                                  (companyDetails?.settings?.sales_invoice_number_prefix_used &&
                                    salesInvoiceNumberStartDisabled)
                                }
                                type='text'
                                formik={formik}
                                required
                              />
                            </div>
                          </div>
                        </div>

                        <div className='content-wrapper bg-white b-radius-25 mt-10'>
                          <div className='d-flex px-5 '>
                            <div
                              className='d-flex justify-content-center px-10 py-3 fs-6 '
                              style={{ width: '100%' }}
                            >
                              <FormattedMessage id='Return sales invoices' />
                            </div>
                          </div>
                          <SeparatorLines lines={2} />
                          <div className='row fv-row m-10 '>
                            <div className='col-md-6'>
                              <Input
                                placeholder={$t('Prefix code')}
                                autoComplete='off'
                                labelId={$t('Prefix code')}
                                name={`return_sales_invoice_number_prefix`}
                                disabled={mode === 'view'}
                                type='text'
                                formik={formik}
                                required
                                onChange={(e: any) => {
                                  formik.setFieldValue(
                                    'return_sales_invoice_number_prefix',
                                    `${e.target.value}`
                                  );
                                  setReturnSalesInvoiceNumberStartDisabled(false);
                                }}
                              />
                            </div>
                            <div className='col-md-6'>
                              <Input
                                placeholder={$t('Invoices start number')}
                                autoComplete='off'
                                labelId={$t('Invoices start number')}
                                name={`return_sales_invoice_number_start`}
                                disabled={
                                  mode === 'view' ||
                                  (companyDetails?.settings
                                    ?.return_sales_invoice_number_prefix_used &&
                                    returnSalesInvoiceNumberStartDisabled)
                                }
                                type='text'
                                formik={formik}
                                required
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
            {displayRequiredSection === 6 && (
              <>
                <div className='p-10 ' style={{ width: '100%' }}>
                  <div className='row'>
                    <div className='col-md-6'>
                      <Input
                        placeholder={formatMessage({ id: 'Account name' })}
                        autoComplete='off'
                        labelId={formatMessage({ id: 'Account name' })}
                        formik={formik}
                        name='account_name'
                        disabled={mode === 'view'}
                      />
                    </div>
                    <div className='col-md-6'>
                      <Input
                        placeholder={formatMessage({ id: 'Bank name' })}
                        autoComplete='off'
                        labelId={formatMessage({ id: 'Bank name' })}
                        formik={formik}
                        name='bank_name'
                        disabled={mode === 'view'}
                      />
                    </div>
                  </div>
                  <div className='row'>
                    <div className='col-md-6'>
                      <Input
                        placeholder={formatMessage({ id: 'Bank account number' })}
                        autoComplete='off'
                        labelId={formatMessage({ id: 'Bank account number' })}
                        formik={formik}
                        name='account_number'
                        disabled={mode === 'view'}
                      />
                    </div>
                    <div className='col-md-6'>
                      <Input
                        placeholder={formatMessage({ id: 'IBAN' })}
                        autoComplete='off'
                        labelId={formatMessage({ id: 'IBAN' })}
                        formik={formik}
                        name='iban'
                        disabled={mode === 'view'}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
            <div className='text-center col-md-6 d-flex gap-5 mt-5'>
              {userCan('update-company') && mode === 'edit' && (
                <button
                  type='submit'
                  id='company-profile-submit'
                  className='btn btn-sm btn-submit w-100 mb-5 badge-clearBlue btn-primary btn-shadow btn-primary-shadow'
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // open modal to confirm if period type is changed
                    if (
                      companyDetails.settings?.period_type !== formik.values.report_period[0].value
                    ) {
                      if (formik.values.report_period[0].value === 3) {
                        setWarningsConfirmation(
                          formatMessage({
                            id: 'Are you sure you want to change period type to be quarterly?',
                          })
                        );
                      } else if (formik.values.report_period[0].value === 2) {
                        setWarningsConfirmation(
                          formatMessage({
                            id: "If you changed period type to be monthly, you won't be able switched to quarterly, until the beginning of a new quarter",
                          })
                        );
                      }
                      setShowWarningsConfirmationModal(true);
                    } else {
                      formik.handleSubmit();
                    }
                  }}
                  disabled={formik.isSubmitting}
                >
                  {!loading && (
                    <span className='indicator-label'>
                      <FormattedMessage id='Save' />
                    </span>
                  )}
                  {loading && (
                    <span className='indicator-progress' style={{ display: 'block' }}>
                      <FormattedMessage id='Please wait...' />
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              )}
              {userCan('update-company') && mode === 'edit' && (
                <button
                  type='button'
                  id='cancel_button'
                  className='btn btn-sm btn-danger w-100 mb-5 btn-shadow btn-danger-shadow'
                  disabled={formik.isSubmitting}
                  onClick={() => {
                    // history.push(PrivateRoutes.companiesList);
                    history.push(`/c/${params.companySlug}/settings/companydetails`);
                  }}
                >
                  <span className='indicator-label'>
                    <FormattedMessage id='Cancel' />
                  </span>
                </button>
              )}
            </div>{' '}
          </Form>
        </FormikProvider>
      </div>

      {/*  ---------------------warnings confirmation Modal-----------------------  */}

      {showWarningsConfirmationModal && warningsConfirmation && (
        <Portal className='full_modal'>
          <AhadDialog
            closeCallBack={
              () => {
                setShowWarningsConfirmationModal(false);
                setLoading(false);
              }

              // history.push(location.pathname.replace('/invite-users', '/users'))
            }
            specialContainerClass='dialog-content-container'
            specialTitleFormat={<></>}
          >
            <>
              <div className='row mb-8 mt-2' style={{ fontSize: '1.5rem', width: '85%' }}>
                {warningsConfirmation}
              </div>
              <div className='col-md-8'>
                <div className='row'>
                  <div className='col-md-6'>
                    <button
                      type='button'
                      className='btn btn-sm w-100 btn-primary btn-shadow btn-primary-shadow'
                      disabled={formik.isSubmitting}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        formik.submitForm();
                        setShowWarningsConfirmationModal(false);
                        setWarningsConfirmation('');
                      }}
                    >
                      {!loading && (
                        <span className='indicator-label'>
                          <FormattedMessage id={'Save'} />
                        </span>
                      )}
                      {loading && (
                        <span className='indicator-progress' style={{ display: 'block' }}>
                          <FormattedMessage id='Please wait...' />
                          <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                      )}
                    </button>
                  </div>
                  <div className='col-md-6'>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowWarningsConfirmationModal(false);
                        setWarningsConfirmation('');
                      }}
                      type='button'
                      id='kt_login_signup_form_cancel_button'
                      className='btn btn-sm btn-danger btn-shadow btn-danger-shadow w-100 badge-gray shadowed'
                    >
                      <FormattedMessage id='Cancel' />
                    </button>
                  </div>
                </div>
              </div>
            </>
          </AhadDialog>
        </Portal>
      )}
      {/*  ---------------------end warnings confirmation Modal-----------------------  */}
      {/*  ---------------------warnings confirmation Modal-----------------------  */}

      {showCategoriesWarning && selectedCategory && (
        <Portal className='full_modal'>
          <AhadDialog
            closeCallBack={
              () => {
                setShowCategoriesWarning(false);
                setLoading(false);
              }

              // history.push(location.pathname.replace('/invite-users', '/users'))
            }
            specialContainerClass='dialog-content-container'
            specialTitleFormat={<></>}
          >
            <>
              <div className='row mb-8 mt-2' style={{ fontSize: '1.5rem', width: '85%' }}>
                <FormattedMessage
                  id={`This category {category_name} doesn't belong to the company's activities, are you sure you want to add it?`}
                  values={{ category_name: `"${selectedCategory.name}"` }}
                />
              </div>
              <div className='col-md-8'>
                <div className='row'>
                  <div className='col-md-6'>
                    <button
                      type='button'
                      className='btn btn-sm w-100 btn-primary btn-shadow btn-primary-shadow'
                      disabled={formik.isSubmitting}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        formik.setFieldValue(
                          'sales_categories',
                          formik.values.sales_categories?.concat(`${selectedCategory.id}`)
                        );
                        setShowCategoriesWarning(false);
                        setSelectedCategory(null);
                      }}
                    >
                      {!loading && (
                        <span className='indicator-label'>
                          <FormattedMessage id={'Approve'} />
                        </span>
                      )}
                      {loading && (
                        <span className='indicator-progress' style={{ display: 'block' }}>
                          <FormattedMessage id='Please wait...' />
                          <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                      )}
                    </button>
                  </div>
                  <div className='col-md-6'>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowCategoriesWarning(false);
                        setSelectedCategory(null);
                      }}
                      type='button'
                      id='kt_login_signup_form_cancel_button'
                      className='btn btn-sm btn-danger btn-shadow btn-danger-shadow w-100 badge-gray shadowed'
                    >
                      <FormattedMessage id='Cancel' />
                    </button>
                  </div>
                </div>
              </div>
            </>
          </AhadDialog>
        </Portal>
      )}
      {/*  ---------------------end warnings confirmation Modal-----------------------  */}
    </>
  );
};
